<ng-container *transloco="let t; read: 'providers.feedPage'">
  <div class="flex h-full min-h-0 flex-col">
    <app-feed-page-header>
      @if (!isCreate && flavor) {
        <app-label-badge feedBadges>{{ flavor }}</app-label-badge>
      }
    </app-feed-page-header>

    <!-- Body area — a relative, non-scrolling wrapper around the scrollable
         body div so the stop-detail drawer (a sibling, not a descendant) can
         sit `absolute inset-0` pinned to it: it overlays exactly the space
         below the sticky header without scrolling along with the body
         content underneath it. -->
    <div class="relative flex min-h-0 flex-1 flex-col">
    <div class="min-h-0 flex-1 overflow-auto px-8 pb-10 pt-6">
      <div class="mx-auto grid max-w-[1180px] items-start gap-6" style="grid-template-columns: minmax(0, 1fr) 380px">
        <!-- Main card: tabs + active pane -->
        <div class="min-w-0 rounded-card bg-card shadow-hairline">
          <app-feed-tab-strip [tabs]="tabs()" [active]="activeTab()" (selected)="selectTab($event)" />
          <div class="flex flex-col gap-5 p-6" role="tabpanel">
            @switch (activeTab()) {
              @case ('general') {
                <app-feed-basics-card [bare]="true" />
              }
              @case ('connection') {
                <div class="flex flex-col gap-5">

                <app-form-field [label]="t('fields.endpoint')">
                  <app-text-input [(ngModel)]="endpoint" name="endpoint" [placeholder]="t('fields.endpointPlaceholder')" required></app-text-input>
                </app-form-field>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <app-form-field [label]="t('fields.requestorRef')">
                    <app-text-input [(ngModel)]="requestorRef" name="requestorRef" [placeholder]="t('fields.requestorRefPlaceholder')" required></app-text-input>
                  </app-form-field>
                  <app-form-field [label]="t('fields.auth') + ' (' + t('fields.authOptional') + ')'">
                    <app-text-input [(ngModel)]="auth" name="auth" [placeholder]="t('fields.authPlaceholder')"></app-text-input>
                  </app-form-field>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <app-form-field [label]="t('fields.flavor')">
                    <app-select [options]="flavorOptions" [(ngModel)]="flavor" name="flavor"></app-select>
                  </app-form-field>
                  <app-form-field [label]="t('fields.requestMethod')">
                    <app-select [options]="requestMethodOptions" [(ngModel)]="requestMethod" name="requestMethod"></app-select>
                  </app-form-field>
                </div>
                </div>
              }
              @case ('stops') {
                <ng-container *transloco="let td; read: 'providers.diagnostics'">
                  @if (isDiagnosticsLoading()) {
                    <span class="text-[13px] text-ink-3">—</span>
                  } @else if (!diagnostics()) {
                    <div class="flex max-w-[520px] flex-col gap-1.5 rounded-control bg-sunken p-5">
                      <span class="text-[14px] font-semibold text-ink-1">{{ t('stops.emptyTitle') }}</span>
                      <p class="m-0 text-[13px] leading-5 text-ink-2">{{ t('stops.emptyBody') }}</p>
                    </div>
                  } @else {
                    <div class="flex flex-wrap items-center justify-between gap-3">
                      <div class="inline-flex rounded-control border border-hairline p-0.5">
                        <button
                          type="button"
                          (click)="toggleIssuesOnly(false)"
                          class="rounded-control px-3 py-1.5 text-[12px] font-semibold"
                          [class.bg-action]="!showIssuesOnly()"
                          [class.text-action-text]="!showIssuesOnly()"
                          [class.text-ink-2]="showIssuesOnly()"
                        >
                          {{ td('allStops') }}
                        </button>
                        <button
                          type="button"
                          (click)="toggleIssuesOnly(true)"
                          class="rounded-control px-3 py-1.5 text-[12px] font-semibold"
                          [class.bg-action]="showIssuesOnly()"
                          [class.text-action-text]="showIssuesOnly()"
                          [class.text-ink-2]="!showIssuesOnly()"
                        >
                          {{ td('issuesOnly') }}
                        </button>
                      </div>
                      <span class="text-[12px] text-ink-3">{{ t('stops.hint') }}</span>
                    </div>

                    <!-- Stops table -->
                    <div class="overflow-x-auto rounded-control border border-hairline">
                      <table class="w-full min-w-[520px] text-[13px]">
                        <thead>
                          <tr class="border-b border-hairline bg-sunken text-left text-[11px] uppercase tracking-wide text-ink-3">
                            <th class="px-3 py-2 font-semibold">{{ td('columns.stopId') }}</th>
                            <th class="px-3 py-2 font-semibold">{{ td('columns.status') }}</th>
                            <th class="px-3 py-2 font-semibold">{{ td('columns.lastFetch') }}</th>
                            <th class="px-3 py-2 font-semibold">{{ td('columns.arrivalsByRoute') }}</th>
                            <th class="px-3 py-2 font-semibold">{{ td('columns.error') }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          @for (stop of visibleStops(); track stop.stopId) {
                            <tr
                              class="cursor-pointer border-b border-hairline last:border-b-0 hover:bg-sunken"
                              (click)="openStopDrawer(stop.stopId, stop.status)"
                            >
                              <td class="px-3 py-2 font-num text-ink-1">{{ stop.stopId }}</td>
                              <td class="px-3 py-2">
                                <span class="flex items-center gap-1.5">
                                  <span
                                    class="h-2 w-2 rounded-full"
                                    [class.bg-success]="stop.status === 'OK'"
                                    [class.bg-danger]="stop.status === 'ERROR'"
                                    [class.bg-warning]="stop.status !== 'OK' && stop.status !== 'ERROR'"
                                  ></span>
                                  {{ stop.status }}
                                </span>
                              </td>
                              <td class="px-3 py-2 text-ink-2">{{ relativeTime(stop.lastFetchAt) }}</td>
                              <td class="px-3 py-2">
                                @if (hasArrivals(stop.arrivalsByRoute)) {
                                  <div class="flex flex-wrap items-center gap-2.5">
                                    @for (route of stop.arrivalsByRoute | keyvalue: unsortedKeyValue; track route.key) {
                                      <span class="flex items-center gap-1.5">
                                        <span class="inline-flex h-5 min-w-[24px] items-center justify-center rounded-[6px] bg-ink-1 px-1.5 font-num text-[11px] font-bold text-card">{{ route.key }}</span>
                                        <span class="font-num text-[13px] font-semibold text-ink-1">{{ route.value }}</span>
                                      </span>
                                    }
                                  </div>
                                } @else {
                                  <span class="text-[12px] text-ink-3">—</span>
                                }
                              </td>
                              <td class="px-3 py-2 text-danger">{{ stop.error }}</td>
                            </tr>
                          }
                        </tbody>
                      </table>
                    </div>
                  }
                </ng-container>
              }
              @case ('files') {
                @if (feed(); as f) {
                  <app-feed-files
                    [feedId]="f.id"
                    [bare]="true"
                    [filesCapability]="provider()?.descriptor?.capabilities?.files ?? null"
                    [providerType]="provider()?.type ?? null"
                    (changed)="refreshFeedSummary()"
                  ></app-feed-files>
                }
              }
            }
          </div>
        </div>

        <!-- Right column: sticky, so health stays visible while long tabs scroll -->
        <div class="sticky top-0 flex flex-col gap-6">
          <!-- Feed health -->
          <div class="flex flex-col gap-4 rounded-card bg-card p-6 shadow-hairline">
            <div class="flex items-center justify-between gap-3">
              <span class="font-display text-[12px] font-semibold uppercase tracking-wide text-ink-2">{{ t('health.title') }}</span>
              @if (!isCreate && !isDiagnosticsLoading()) {
                <app-label-badge [dot]="healthDot()">{{ healthLabel() }}</app-label-badge>
              }
            </div>
            @if (isCreate) {
              <div class="flex flex-col gap-1.5">
                <span class="text-[14px] font-semibold text-ink-1">{{ t('health.createTitle') }}</span>
                <p class="m-0 text-[13px] leading-5 text-ink-2">{{ t('health.createBody') }}</p>
              </div>
            } @else if (isDiagnosticsLoading()) {
              <span class="text-[13px] text-ink-3">—</span>
            } @else if (!diagnostics()) {
              <div class="flex flex-col gap-1.5">
                <span class="text-[14px] font-semibold text-ink-1">{{ t('health.noSnapshotTitle') }}</span>
                <p class="m-0 text-[13px] leading-5 text-ink-2">{{ t('health.noSnapshotBody') }}</p>
              </div>
            } @else {
              @let d = diagnostics()!;
              @let h = health();
              @if (isStale()) {
                <p class="m-0 rounded-control bg-danger-surface px-3 py-2 text-[12px] leading-[18px] text-danger">
                  <span class="font-bold">{{ t('health.staleTitle', { minutes: staleMinutes() }) }}</span> {{ t('health.staleSuffix') }}
                </p>
              }
              <div class="flex flex-wrap items-baseline gap-x-8 gap-y-2">
                <span class="flex items-baseline gap-2">
                  <span class="font-display text-[32px] font-semibold leading-9 text-ink-1">{{ h.total }}</span>
                  <span class="text-[13px] text-ink-3">{{ t('health.stopsLabel') }}</span>
                </span>
                <span class="flex items-baseline gap-2">
                  <span class="font-display text-[32px] font-semibold leading-9 text-ink-1">{{ h.arrivals }}</span>
                  <span class="text-[13px] text-ink-3">{{ t('health.arrivalsLabel') }}</span>
                </span>
              </div>
              <app-stacked-bar [segments]="stopSegments()" />
              <div class="border-t border-hairline pt-3 text-[12px] leading-[18px] text-ink-3">
                {{ t('health.siriFooter', { generated: relativeTime(d.generatedAt), received: relativeTime(d.receivedAt) }) }}
              </div>
            }
          </div>

          <app-feed-identity-card />
        </div>
      </div>
    </div>

    <!-- Stop detail drawer -->
    @if (isDrawerOpen()) {
    <ng-container *transloco="let ts; read: 'providers.stopDrawer'">
      <!-- Scrim — fixed below the top bar (top-16 = its 64px height), so it
           covers the sidebar and reaches the true bottom of the viewport
           without covering the top bar itself. Below the drawer, above the
           page; click closes. Fades in place; must not share the drawer's
           slide animation. -->
      <div
        animate.enter="stop-scrim-in"
        animate.leave="stop-scrim-out"
        class="fixed inset-x-0 top-16 bottom-0 z-40 bg-black/[0.12]"
        (click)="onDrawerScrimClick()"
      ></div>

      <aside
        animate.enter="stop-drawer-in"
        animate.leave="stop-drawer-out"
        class="fixed right-0 top-16 bottom-0 z-50 flex w-[680px] max-w-full flex-col border-l border-hairline bg-card shadow-overlay"
      >
        <!-- Header -->
        <div class="flex h-[58px] flex-none items-center justify-between border-b border-hairline px-5">
          <span class="text-[15px] font-bold text-ink-1">{{ ts('title') }}</span>
          <button
            type="button"
            (click)="closeStopDrawer()"
            [title]="ts('close')"
            class="flex h-[30px] w-[30px] items-center justify-center rounded-control border-none bg-transparent text-ink-3 hover:bg-sunken"
          >
            <app-icon name="icon-x" [size]="17" />
          </button>
        </div>

        <!-- Sub-header: identity + freshness -->
        <div class="flex flex-none items-start justify-between gap-4 border-b border-hairline px-5 py-4">
          <div class="flex min-w-0 flex-col gap-2">
            <div class="flex items-center gap-3">
              <span class="font-display text-[16px] font-bold text-ink-1">{{ stopDrawer()?.stopId }}</span>
              <app-label-badge [dot]="drawerStopStatusDot()">{{ stopDrawer()?.diagnosticsStatus }}</app-label-badge>
            </div>
            <div class="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[12px] text-ink-3">
              <span class="flex items-center gap-1.5">
                @if (isFetching()) {
                  <span class="stop-drawer-pulse h-[7px] w-[7px] flex-none rounded-full bg-ink-3"></span>
                  <span class="stop-drawer-pulse">{{ ts('live.fetching') }}</span>
                } @else if (autoRefresh()) {
                  <span class="h-[7px] w-[7px] flex-none rounded-full bg-success"></span>
                  {{ ts('live.live') }}
                } @else {
                  <span class="h-[7px] w-[7px] flex-none rounded-full bg-ink-3"></span>
                  {{ ts('live.paused') }}
                }
              </span>
              @if (drawerPublishedAt()) {
                <span>{{ ts('live.lastPublished', { time: relativeTime(drawerPublishedAt()!) }) }}</span>
              }
              <span>{{ departureRows().length }}</span>
            </div>
          </div>

          <div class="flex flex-none flex-col items-end gap-2">
            <span class="text-[11px] text-ink-3">
              @if (isFetching()) {
                {{ ts('refresh.fetching') }}
              } @else if (lastError() && content()) {
                {{ ts('live.failed', { seconds: refreshCountdown() }) }}
              } @else if (autoRefresh()) {
                {{ ts('refresh.next', { seconds: refreshCountdown() }) }}
              } @else {
                {{ ts('refresh.fetched', { time: relativeTime(drawerPublishedAt() ?? '') }) }}
              }
            </span>
            <div class="flex items-center gap-2">
              <button
                type="button"
                (click)="toggleDrawerAutoRefresh()"
                class="h-[30px] rounded-control border border-hairline px-3 text-[12px] font-semibold"
                [class.bg-sunken]="autoRefresh()"
                [class.text-ink-1]="autoRefresh()"
                [class.bg-card]="!autoRefresh()"
                [class.text-ink-2]="!autoRefresh()"
              >
                {{ autoRefresh() ? ts('refresh.on') : ts('refresh.off') }}
              </button>
              <button
                type="button"
                (click)="refreshDrawerContent()"
                class="h-[30px] rounded-control border border-hairline bg-card px-3 text-[12px] font-semibold text-ink-1 hover:bg-sunken"
              >
                {{ ts('refresh.button') }}
              </button>
            </div>
          </div>
        </div>

        @if (lastError() && content()) {
          <div class="flex-none border-b border-hairline bg-danger-surface px-5 py-2 text-[12px] font-semibold text-danger">
            {{ ts('live.failed', { seconds: refreshCountdown() }) }}
          </div>
        }

        <!-- Tabs -->
        <div class="flex flex-none items-stretch gap-1 border-b border-hairline px-5">
          @for (tab of ['departures', 'messages', 'raw']; track tab) {
            <button
              type="button"
              (click)="setDrawerTab($any(tab))"
              class="relative flex items-center gap-2 border-none bg-transparent px-3.5 py-3"
            >
              <span
                class="text-[13px] font-bold uppercase tracking-wide"
                [class.text-ink-1]="drawerTab() === tab"
                [class.text-ink-3]="drawerTab() !== tab"
                >{{ ts('tabs.' + tab) }}</span
              >
              @if (tab === 'messages') {
                <span
                  class="inline-flex min-w-5 items-center justify-center rounded-pill px-1.5 py-0.5 text-[11px] font-semibold"
                  [class.bg-action]="drawerTab() === 'messages'"
                  [class.text-action-text]="drawerTab() === 'messages'"
                  [class.bg-sunken]="drawerTab() !== 'messages'"
                  [class.text-ink-2]="drawerTab() !== 'messages'"
                  >{{ messagesCount() }}</span
                >
              }
              @if (drawerTab() === tab) {
                <div class="absolute inset-x-3 -bottom-px h-0.5 rounded bg-action"></div>
              }
            </button>
          }
        </div>

        <!-- Tab body -->
        <div class="min-h-0 flex-1 overflow-auto">
          @if (isFetching() && !content() && !lastError()) {
            <div class="p-5 text-[14px] text-ink-3">{{ ts('empty.loading') }}</div>
          } @else if (lastError() && !content()) {
            <div class="flex flex-col items-start gap-3 p-5">
              <span class="text-[14px] font-semibold text-ink-1">{{ ts('empty.loadFailed') }}</span>
              <button
                type="button"
                (click)="refreshDrawerContent()"
                class="rounded-control border border-hairline bg-card px-3.5 py-2 text-[13px] font-semibold text-ink-1 hover:bg-sunken"
              >
                {{ ts('empty.retry') }}
              </button>
            </div>
          } @else if (!content()) {
            <div class="max-w-[460px] p-5">
              <div class="text-[14px] font-semibold text-ink-1">{{ ts('empty.nothingPublished') }}</div>
              <p class="m-0 mt-1.5 text-[13px] leading-[20px] text-ink-3">{{ ts('empty.nothingPublishedBody') }}</p>
            </div>
          } @else {
            @switch (drawerTab()) {
              @case ('departures') {
                <div class="flex min-h-full flex-col">
                <table class="w-full text-[13px]">
                  <thead>
                    <tr class="h-[46px] border-b border-hairline bg-card text-left text-[12px] font-semibold uppercase tracking-wide text-ink-3">
                      <th class="w-[56px] px-3">{{ ts('columns.line') }}</th>
                      <th class="px-3">{{ ts('columns.destination') }}</th>
                      <th class="w-[62px] px-3">{{ ts('columns.quay') }}</th>
                      <th class="w-[70px] px-3">{{ ts('columns.aimed') }}</th>
                      <th class="w-[100px] px-3">{{ ts('columns.expected') }}</th>
                      <th class="w-[70px] px-3">{{ ts('columns.delay') }}</th>
                      <th class="w-[128px] px-3">{{ ts('columns.status') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (row of departureRows(); track row.departureId) {
                      <tr
                        class="min-h-[42px] border-b border-hairline hover:bg-sunken"
                        [class.bg-[#FCFCFC]]="row.status === 'cancelled'"
                      >
                        <td class="px-3 py-2.5">
                          <span class="inline-flex h-[22px] min-w-[26px] items-center justify-center rounded-[6px] bg-ink-1 px-1.5 font-num text-[12px] font-bold text-card">{{ row.line }}</span>
                        </td>
                        <td class="px-3 py-2.5">
                          <div
                            class="text-ink-1"
                            [class.line-through]="row.status === 'cancelled'"
                            [class.text-ink-3]="row.status === 'cancelled'"
                          >
                            {{ row.destination }}
                          </div>
                          @if (row.isRealtimeOnly || row.isAtStop) {
                            <div class="mt-1 flex flex-wrap gap-1">
                              @if (row.isRealtimeOnly) {
                                <span class="inline-flex h-5 items-center rounded-control border border-hairline px-1.5 text-[11px] text-ink-3">{{ ts('flags.realtimeOnly') }}</span>
                              }
                              @if (row.isAtStop) {
                                <span class="inline-flex h-5 items-center rounded-control border border-hairline px-1.5 text-[11px] text-ink-3">{{ ts('flags.atStop') }}</span>
                              }
                            </div>
                          } @else if (row.via) {
                            <div class="mt-0.5 truncate text-[12px] text-ink-3">{{ row.via }}</div>
                          }
                        </td>
                        <td class="px-3 py-2.5 text-ink-2">{{ row.quay }}</td>
                        <td class="px-3 py-2.5 font-num tabular-nums text-ink-2">{{ formatClock(row.aimedDepartureTime) }}</td>
                        <td
                          class="px-3 py-2.5 font-num tabular-nums"
                          [class.text-ink-1]="row.status === 'on-time'"
                          [class.text-danger]="row.status === 'late'"
                          [class.text-warning]="row.status === 'early'"
                          [class.text-ink-3]="row.status === 'no-prediction'"
                          [class.line-through]="row.status === 'cancelled'"
                        >
                          {{ row.status === 'cancelled' || !row.expectedDepartureTime ? '—' : formatClock(row.expectedDepartureTime) }}
                        </td>
                        <td
                          class="whitespace-nowrap px-3 py-2.5 font-num tabular-nums"
                          [class.text-danger]="row.status === 'late'"
                          [class.text-warning]="row.status === 'early'"
                        >
                          {{ formatDelay(row.delayMinutes) }}
                        </td>
                        <td class="px-3 py-2.5">
                          @switch (row.status) {
                            @case ('on-time') { <app-label-badge dot="green">{{ ts('status.onTime') }}</app-label-badge> }
                            @case ('late') { <app-label-badge dot="red">{{ ts('status.late') }}</app-label-badge> }
                            @case ('early') { <app-label-badge dot="orange">{{ ts('status.early') }}</app-label-badge> }
                            @case ('cancelled') { <app-label-badge dot="red">{{ ts('status.cancelled') }}</app-label-badge> }
                            @default { <app-label-badge>{{ ts('status.noPrediction') }}</app-label-badge> }
                          }
                        </td>
                      </tr>
                    } @empty {
                      <tr>
                        <td colspan="7" class="py-8 text-center text-[14px] text-ink-3">{{ ts('empty.noDepartures') }}</td>
                      </tr>
                    }
                  </tbody>
                </table>
                <p class="mt-auto border-t border-hairline px-5 pb-[18px] pt-3.5 text-[12px] leading-[18px] text-ink-3">{{ ts('footnote') }}</p>
                </div>
              }
              @case ('messages') {
                <div class="flex flex-col gap-6 bg-sunken px-6 py-5">
                  <div class="flex flex-col gap-2.5">
                    <div class="text-[12px] font-semibold uppercase tracking-wide text-ink-3">{{ ts('messages.information') }}</div>
                    @if (!liveInformationMessages().length) {
                      <p class="m-0 text-[13px] text-ink-3">{{ ts('messages.none') }}</p>
                    }
                    @for (msg of liveInformationMessages(); track msg.messageId) {
                      <div class="flex flex-col gap-2.5 rounded-[8px] border border-hairline bg-card p-4">
                        <div class="flex flex-wrap items-center gap-2">
                          <app-label-badge [dot]="msg.messagePriority === 'EMERGENCY' ? 'red' : null">
                            {{ msg.messagePriority === 'EMERGENCY' ? ts('messages.emergency') : ts('messages.standard') }}
                          </app-label-badge>
                          @if (msg.messageTargetArea) {
                            <app-label-badge>{{ msg.messageTargetArea }}</app-label-badge>
                          }
                          @if (msg.messageBehaviour) {
                            <app-label-badge>{{ msg.messageBehaviour }}</app-label-badge>
                          }
                          <span class="ml-auto text-[11px] text-ink-3">{{ msg.messageId }}</span>
                        </div>
                        <p class="m-0 text-[14px] leading-[21px] text-ink-1">{{ pickLocalized(msg.messageText) }}</p>
                        <div class="flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-ink-3">
                          <span>{{ ts('messages.window', { from: formatClock(msg.earliestDisplayTime), to: formatClock(msg.latestDisplayTime) }) }}</span>
                          <span>{{ ts('messages.actionPriority', { priority: msg.messageActionPriority ?? 0 }) }}</span>
                        </div>
                      </div>
                    }
                  </div>

                  <div class="flex flex-col gap-2.5">
                    <div class="text-[12px] font-semibold uppercase tracking-wide text-ink-3">{{ ts('messages.journey') }}</div>
                    @if (!liveJourneyMessages().length) {
                      <p class="m-0 text-[13px] text-ink-3">{{ ts('messages.noneJourney') }}</p>
                    }
                    @for (msg of liveJourneyMessages(); track msg.messageId) {
                      <div class="flex flex-col gap-2.5 rounded-[8px] border border-hairline bg-card p-4">
                        <div class="flex flex-wrap items-center gap-2 text-[12px] text-ink-2">
                          @if (msg.messageBehaviour) {
                            <app-label-badge>{{ msg.messageBehaviour }}</app-label-badge>
                          }
                          <span class="ml-auto text-[11px] text-ink-3">{{ msg.messageId }}</span>
                        </div>
                        <p class="m-0 text-[14px] leading-[21px] text-ink-1">{{ pickLocalized(msg.messageText) }}</p>
                        <div class="flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-ink-3">
                          <span>{{ ts('messages.window', { from: formatClock(msg.earliestDisplayTime), to: formatClock(msg.latestDisplayTime) }) }}</span>
                          <span>{{ ts('messages.actionPriority', { priority: msg.messageActionPriority ?? 0 }) }}</span>
                        </div>
                      </div>
                    }
                  </div>
                </div>
              }
              @case ('raw') {
                <div class="flex flex-col gap-3 bg-sunken px-6 py-5">
                  <div class="flex items-center justify-end gap-3">
                    <button
                      type="button"
                      (click)="copyRawJson()"
                      class="flex flex-none items-center gap-1.5 rounded-control border border-hairline bg-card px-3 py-2 text-[13px] font-semibold text-ink-1 hover:bg-sunken"
                    >
                      <app-icon name="icon-clipboard-check" [size]="14" />
                      {{ ts('raw.copy') }}
                    </button>
                  </div>
                  <pre class="overflow-auto rounded-[8px] border border-hairline bg-card p-4 font-mono text-[12px] leading-[19px] text-ink-1">{{ content() | json }}</pre>
                </div>
              }
            }
          }
        </div>
      </aside>
    </ng-container>
    }
    </div>
  </div>
</ng-container>
